import React, { useCallback, useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Building2, Layers, ClipboardCheck, Plus, Pencil, Trash2, RefreshCw, CheckCircle2, XCircle, Ban, ChevronRight, ShieldCheck, AlertCircle, FileCheck, KeyRound, FileText, Package, AlertTriangle, BarChart3, Handshake } from "lucide-react";
import { EligibilityTab, AuthorizationsTab, ClaimsTab, BatchesTab, DenialsTab, ReportsTab } from "./AdminConveniosWorkflowTabs";
import { ContractsSubTab } from "./AdminConveniosContractsTab";

const SEGMENTS = [
  { v: "", l: "—" },
  { v: "ambulatorial", l: "Ambulatorial" },
  { v: "hospitalar", l: "Hospitalar" },
  { v: "referencia", l: "Referência" },
  { v: "odontologico", l: "Odontológico" },
  { v: "outros", l: "Outros" },
];

const CRED_STATUSES = [
  { v: "pending", l: "Pendente", cls: "bg-amber-100 text-amber-800" },
  { v: "active", l: "Ativo", cls: "bg-emerald-100 text-emerald-800" },
  { v: "suspended", l: "Suspenso", cls: "bg-slate-200 text-slate-700" },
  { v: "expired", l: "Expirado", cls: "bg-red-100 text-red-800" },
  { v: "rejected", l: "Rejeitado", cls: "bg-red-100 text-red-800" },
];

const STATUS_MAP = Object.fromEntries(CRED_STATUSES.map(s => [s.v, s]));

// ============================================ Modal: New/Edit Insurer
const InsurerModal = ({ open, insurer, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const editing = !!insurer;

  useEffect(() => {
    if (!open) return;
    setForm(insurer || {
      name: "", slug: "", ans_registration: "", active: true,
      teleconsultation_enabled: false, logo_url: "", notes: "",
    });
  }, [open, insurer]);

  if (!open) return null;
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!(form.name || "").trim()) return toast.error("Nome obrigatório");
    if (!editing && !/^[a-z0-9\-]+$/.test(form.slug || "")) {
      return toast.error("Slug deve ter só letras minúsculas, números e hífen");
    }
    setBusy(true);
    try {
      if (editing) {
        const payload = {
          name: form.name,
          ans_registration: form.ans_registration || null,
          active: form.active,
          teleconsultation_enabled: form.teleconsultation_enabled,
          logo_url: form.logo_url || null,
          notes: form.notes || null,
        };
        await api.patch(`/admin/convenios/insurers/${insurer.id}`, payload);
        toast.success("Operadora atualizada");
      } else {
        await api.post("/admin/convenios/insurers", {
          name: form.name.trim(),
          slug: form.slug.trim(),
          ans_registration: form.ans_registration || null,
          active: form.active,
          teleconsultation_enabled: form.teleconsultation_enabled,
          logo_url: form.logo_url || null,
          notes: form.notes || null,
        });
        toast.success("Operadora criada");
      }
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="insurer-modal">
      <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[#0B2B62]">
            <Building2 className="w-5 h-5" />
            <div className="font-display font-bold text-lg">{editing ? "Editar operadora" : "Nova operadora"}</div>
          </div>
          <button onClick={onClose}><XCircle className="w-5 h-5 text-slate-400 hover:text-slate-700" /></button>
        </div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-3 gap-3">
            <label className="col-span-2 block">
              <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Nome *</span>
              <input value={form.name || ""} onChange={e => set("name", e.target.value)}
                data-testid="insurer-form-name"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </label>
            <label className="block">
              <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Registro ANS</span>
              <input value={form.ans_registration || ""} onChange={e => set("ans_registration", e.target.value)}
                data-testid="insurer-form-ans"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </label>
          </div>
          {!editing && (
            <label className="block">
              <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Slug (URL) *</span>
              <input value={form.slug || ""} onChange={e => set("slug", e.target.value.toLowerCase())}
                placeholder="unimed-sp"
                data-testid="insurer-form-slug"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </label>
          )}
          <label className="block">
            <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">URL da logo</span>
            <input value={form.logo_url || ""} onChange={e => set("logo_url", e.target.value)}
              placeholder="https://…"
              data-testid="insurer-form-logo"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </label>
          <label className="block">
            <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Notas internas</span>
            <textarea value={form.notes || ""} onChange={e => set("notes", e.target.value)}
              rows={3} maxLength={1000}
              data-testid="insurer-form-notes"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </label>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 select-none">
              <input type="checkbox" checked={form.active} onChange={e => set("active", e.target.checked)}
                data-testid="insurer-form-active" className="rounded border-slate-300" />
              Ativa
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 select-none">
              <input type="checkbox" checked={form.teleconsultation_enabled}
                onChange={e => set("teleconsultation_enabled", e.target.checked)}
                data-testid="insurer-form-tele" className="rounded border-slate-300" />
              Teleconsulta liberada
            </label>
          </div>
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-900 flex gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>Deixe <b>Teleconsulta liberada</b> desmarcada enquanto a RAF não tiver contrato oficial com essa operadora — evita expor cobertura inexistente pra família.</span>
          </div>
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancelar</button>
          <button onClick={save} disabled={busy}
            data-testid="insurer-form-save"
            className="px-4 py-2 text-sm bg-[#0B2B62] text-white rounded-md font-semibold disabled:opacity-40 hover:bg-slate-800 inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" /> {busy ? "Salvando…" : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================ Modal: New/Edit Plan
const PlanModal = ({ open, plan, insurers, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const editing = !!plan;

  useEffect(() => {
    if (!open) return;
    setForm(plan || {
      insurer_id: insurers?.[0]?.id || "",
      name: "", code: "", segment: "", active: true,
      teleconsultation_covered: true, requires_authorization: false, notes: "",
    });
  }, [open, plan, insurers]);

  if (!open) return null;
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!(form.name || "").trim()) return toast.error("Nome obrigatório");
    if (!editing && !form.insurer_id) return toast.error("Selecione a operadora");
    setBusy(true);
    try {
      if (editing) {
        const payload = {
          name: form.name, code: form.code || null,
          segment: form.segment || null, active: form.active,
          teleconsultation_covered: form.teleconsultation_covered,
          requires_authorization: form.requires_authorization,
          notes: form.notes || null,
        };
        await api.patch(`/admin/convenios/plans/${plan.id}`, payload);
        toast.success("Plano atualizado");
      } else {
        await api.post("/admin/convenios/plans", {
          insurer_id: form.insurer_id,
          name: form.name.trim(),
          code: form.code || null,
          segment: form.segment || null,
          active: form.active,
          teleconsultation_covered: form.teleconsultation_covered,
          requires_authorization: form.requires_authorization,
          notes: form.notes || null,
        });
        toast.success("Plano criado");
      }
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="plan-modal">
      <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[#0B2B62]">
            <Layers className="w-5 h-5" />
            <div className="font-display font-bold text-lg">{editing ? "Editar plano" : "Novo plano"}</div>
          </div>
          <button onClick={onClose}><XCircle className="w-5 h-5 text-slate-400 hover:text-slate-700" /></button>
        </div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          {!editing && (
            <label className="block">
              <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Operadora *</span>
              <select value={form.insurer_id || ""} onChange={e => set("insurer_id", e.target.value)}
                data-testid="plan-form-insurer"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
                {insurers.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </label>
          )}
          <div className="grid grid-cols-3 gap-3">
            <label className="col-span-2 block">
              <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Nome do plano *</span>
              <input value={form.name || ""} onChange={e => set("name", e.target.value)}
                data-testid="plan-form-name"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </label>
            <label className="block">
              <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Código</span>
              <input value={form.code || ""} onChange={e => set("code", e.target.value)}
                data-testid="plan-form-code"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Segmentação ANS</span>
            <select value={form.segment || ""} onChange={e => set("segment", e.target.value)}
              data-testid="plan-form-segment"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
              {SEGMENTS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-3 gap-3 pt-2">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.active} onChange={e => set("active", e.target.checked)}
                data-testid="plan-form-active" className="rounded border-slate-300" />
              Ativo
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.teleconsultation_covered}
                onChange={e => set("teleconsultation_covered", e.target.checked)}
                data-testid="plan-form-tele" className="rounded border-slate-300" />
              Cobre teleconsulta
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.requires_authorization}
                onChange={e => set("requires_authorization", e.target.checked)}
                data-testid="plan-form-auth" className="rounded border-slate-300" />
              Exige autorização
            </label>
          </div>
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancelar</button>
          <button onClick={save} disabled={busy}
            data-testid="plan-form-save"
            className="px-4 py-2 text-sm bg-[#0B2B62] text-white rounded-md font-semibold disabled:opacity-40 hover:bg-slate-800 inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" /> {busy ? "Salvando…" : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================ Sub-tab: Operadoras
const InsurersSubTab = ({ insurers, onReload }) => {
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const remove = async (ins) => {
    if (!window.confirm(`Excluir operadora "${ins.name}"?\n\nRemove planos e não é permitido se há credenciamentos ativos/pendentes.`)) return;
    try {
      await api.delete(`/admin/convenios/insurers/${ins.id}`);
      toast.success(`Operadora ${ins.name} excluída.`);
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const toggle = async (ins, key) => {
    try {
      await api.patch(`/admin/convenios/insurers/${ins.id}`, { [key]: !ins[key] });
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">{insurers.length} operadora(s) cadastrada(s)</div>
        <button onClick={() => setCreating(true)}
          data-testid="admin-insurers-new"
          className="text-xs px-3 py-1.5 bg-amber-400 hover:bg-amber-300 text-[#0B2B62] font-semibold rounded-md inline-flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> Nova operadora
        </button>
      </div>
      <div className="border border-slate-200 rounded-xl bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Nome</th>
              <th className="text-left px-4 py-3">ANS</th>
              <th className="text-left px-4 py-3">Ativa</th>
              <th className="text-left px-4 py-3">Teleconsulta</th>
              <th className="text-left px-4 py-3">Planos</th>
              <th className="text-left px-4 py-3">Cred.</th>
              <th className="text-right px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {insurers.map(ins => (
              <tr key={ins.id} className="border-t" data-testid={`insurer-row-${ins.slug}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-[#0B2B62]">{ins.name}</div>
                  <div className="text-[11px] text-slate-500">{ins.slug}</div>
                </td>
                <td className="px-4 py-3 text-slate-600 text-xs font-mono">{ins.ans_registration || "—"}</td>
                <td className="px-4 py-3">
                  <button onClick={() => toggle(ins, "active")}
                    data-testid={`insurer-toggle-active-${ins.slug}`}
                    className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                      ins.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"
                    }`}>{ins.active ? "Sim" : "Não"}</button>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => toggle(ins, "teleconsultation_enabled")}
                    data-testid={`insurer-toggle-tele-${ins.slug}`}
                    title={ins.teleconsultation_enabled
                      ? "Teleconsulta liberada — profissionais aprovados aparecem pra família"
                      : "Bloqueado — nenhum profissional aparece como credenciado pra família"}
                    className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                      ins.teleconsultation_enabled ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"
                    }`}>{ins.teleconsultation_enabled ? "Ok" : "Bloqueada"}</button>
                </td>
                <td className="px-4 py-3 text-slate-700 text-xs tabular-nums">{ins.plans_count || 0}</td>
                <td className="px-4 py-3 text-slate-700 text-xs tabular-nums">
                  <span className="text-emerald-700 font-semibold">{ins.active_credentials_count || 0}</span>
                  {ins.pending_credentials_count > 0 && (
                    <span className="text-amber-700 ml-1">(+{ins.pending_credentials_count} pend.)</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1.5 justify-end">
                    <button onClick={() => setEditing(ins)}
                      data-testid={`insurer-edit-${ins.slug}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-slate-700 border border-slate-300 rounded-md hover:bg-slate-50">
                      <Pencil className="w-3.5 h-3.5" /> Editar
                    </button>
                    <button onClick={() => remove(ins)}
                      data-testid={`insurer-delete-${ins.slug}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-red-600 border border-red-200 rounded-md hover:bg-red-50">
                      <Trash2 className="w-3.5 h-3.5" /> Excluir
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <InsurerModal open={creating} onClose={() => setCreating(false)} onSaved={onReload} />
      <InsurerModal open={!!editing} insurer={editing} onClose={() => setEditing(null)} onSaved={onReload} />
    </div>
  );
};

// ============================================ Sub-tab: Planos
const PlansSubTab = ({ insurers }) => {
  const [plans, setPlans] = useState([]);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? { insurer_id: filter } : {};
      const { data } = await api.get("/admin/convenios/plans", { params });
      setPlans(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const remove = async (p) => {
    if (!window.confirm(`Excluir plano "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/convenios/plans/${p.id}`);
      toast.success("Plano excluído");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const insurerById = useMemo(() => Object.fromEntries(insurers.map(i => [i.id, i])), [insurers]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <select value={filter} onChange={e => setFilter(e.target.value)}
          data-testid="plans-filter-insurer"
          className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
          <option value="">Todas as operadoras ({plans.length})</option>
          {insurers.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
        <button onClick={() => setCreating(true)}
          data-testid="admin-plans-new"
          className="text-xs px-3 py-1.5 bg-amber-400 hover:bg-amber-300 text-[#0B2B62] font-semibold rounded-md inline-flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> Novo plano
        </button>
      </div>
      {loading ? <div className="p-6 text-slate-500 text-sm">Carregando…</div>
      : plans.length === 0 ? (
        <div className="p-8 border border-dashed border-slate-300 rounded-xl bg-slate-50 text-slate-500 text-sm text-center">
          Nenhum plano cadastrado {filter ? "pra essa operadora" : "ainda"}.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-xl bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Plano</th>
                <th className="text-left px-4 py-3">Operadora</th>
                <th className="text-left px-4 py-3">Segmento</th>
                <th className="text-left px-4 py-3">Teleconsulta</th>
                <th className="text-left px-4 py-3">Autorização</th>
                <th className="text-left px-4 py-3">Ativo</th>
                <th className="text-right px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {plans.map(p => (
                <tr key={p.id} className="border-t" data-testid={`plan-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#0B2B62]">{p.name}</div>
                    {p.code && <div className="text-[11px] text-slate-500 font-mono">{p.code}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-700 text-xs">{insurerById[p.insurer_id]?.name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs uppercase">{p.segment || "—"}</td>
                  <td className="px-4 py-3">
                    {p.teleconsultation_covered ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-slate-400" />}
                  </td>
                  <td className="px-4 py-3">
                    {p.requires_authorization ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold">Sim</span> : <span className="text-slate-400 text-xs">Não</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${p.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>
                      {p.active ? "Sim" : "Não"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1.5 justify-end">
                      <button onClick={() => setEditing(p)}
                        data-testid={`plan-edit-${p.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-slate-700 border border-slate-300 rounded-md hover:bg-slate-50">
                        <Pencil className="w-3.5 h-3.5" /> Editar
                      </button>
                      <button onClick={() => remove(p)}
                        data-testid={`plan-delete-${p.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-red-600 border border-red-200 rounded-md hover:bg-red-50">
                        <Trash2 className="w-3.5 h-3.5" /> Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <PlanModal open={creating} insurers={insurers} onClose={() => setCreating(false)} onSaved={load} />
      <PlanModal open={!!editing} plan={editing} insurers={insurers} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
};

// ============================================ Sub-tab: Credenciamentos
const CredentialsSubTab = () => {
  const [creds, setCreds] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const { data } = await api.get("/admin/convenios/credentials", { params });
      setCreds(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (c, newStatus) => {
    let extra = {};
    if (newStatus === "rejected") {
      const reason = window.prompt(`Motivo da rejeição:`, "");
      if (reason === null) return;
      extra = { rejection_reason: reason.trim() };
    }
    try {
      await api.patch(`/admin/convenios/credentials/${c.id}`, { status: newStatus, ...extra });
      toast.success(`Status: ${STATUS_MAP[newStatus].l}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Excluir credenciamento "${c.professional_name} · ${c.insurer_name}"?`)) return;
    try {
      await api.delete(`/admin/convenios/credentials/${c.id}`);
      toast.success("Excluído");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex items-center gap-1 border border-slate-200 rounded-full p-1 bg-white">
          {[["", "Todos"], ...CRED_STATUSES.map(s => [s.v, s.l])].map(([v, l]) => (
            <button key={v} onClick={() => setStatusFilter(v)}
              data-testid={`creds-filter-${v || "todos"}`}
              className={`text-xs px-3 py-1 rounded-full ${statusFilter === v ? "bg-[#0B2B62] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              {l}
            </button>
          ))}
        </div>
        <button onClick={load} data-testid="creds-refresh"
          className="text-xs px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50 inline-flex items-center gap-1.5">
          <RefreshCw className="w-3.5 h-3.5" /> Atualizar
        </button>
      </div>
      {loading ? <div className="p-6 text-slate-500 text-sm">Carregando…</div>
      : creds.length === 0 ? (
        <div className="p-8 border border-dashed border-slate-300 rounded-xl bg-slate-50 text-slate-500 text-sm text-center">
          Nenhum credenciamento {statusFilter ? `com status "${STATUS_MAP[statusFilter]?.l}"` : "cadastrado ainda"}.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-xl bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Profissional</th>
                <th className="text-left px-4 py-3">Operadora / Plano</th>
                <th className="text-left px-4 py-3">Especialidade</th>
                <th className="text-left px-4 py-3">Nº credencial</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {creds.map(c => (
                <tr key={c.id} className="border-t" data-testid={`cred-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#0B2B62]">{c.professional_name || "?"}</div>
                    <div className="text-[11px] text-slate-500">{c.professional_email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-slate-700">{c.insurer_name}</div>
                    {c.plan_name && <div className="text-[11px] text-slate-500">{c.plan_name}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-700 text-xs">{c.specialty}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs font-mono">{c.credential_number}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase font-bold tracking-widest ${STATUS_MAP[c.status]?.cls || ""}`}>
                      {STATUS_MAP[c.status]?.l || c.status}
                    </span>
                    {c.rejection_reason && (
                      <div className="text-[10px] text-red-600 mt-1 italic">"{c.rejection_reason}"</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1.5 justify-end flex-wrap">
                      {c.status === "pending" && (
                        <>
                          <button onClick={() => changeStatus(c, "active")}
                            data-testid={`cred-approve-${c.id}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-emerald-700 border border-emerald-300 rounded-md hover:bg-emerald-50">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Aprovar
                          </button>
                          <button onClick={() => changeStatus(c, "rejected")}
                            data-testid={`cred-reject-${c.id}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-red-600 border border-red-200 rounded-md hover:bg-red-50">
                            <XCircle className="w-3.5 h-3.5" /> Rejeitar
                          </button>
                        </>
                      )}
                      {c.status === "active" && (
                        <button onClick={() => changeStatus(c, "suspended")}
                          data-testid={`cred-suspend-${c.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-slate-700 border border-slate-300 rounded-md hover:bg-slate-50">
                          <Ban className="w-3.5 h-3.5" /> Suspender
                        </button>
                      )}
                      {(c.status === "suspended" || c.status === "expired") && (
                        <button onClick={() => changeStatus(c, "active")}
                          data-testid={`cred-reactivate-${c.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-emerald-700 border border-emerald-300 rounded-md hover:bg-emerald-50">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Reativar
                        </button>
                      )}
                      <button onClick={() => remove(c)}
                        data-testid={`cred-delete-${c.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-red-600 border border-red-200 rounded-md hover:bg-red-50">
                        <Trash2 className="w-3.5 h-3.5" /> Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ============================================ Main Panel
const AdminConveniosPanel = () => {
  const [sub, setSub] = useState("insurers"); // insurers | plans | credentials
  const [insurers, setInsurers] = useState([]);

  const loadInsurers = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/convenios/insurers");
      setInsurers(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  }, []);

  useEffect(() => { loadInsurers(); }, [loadInsurers]);

  const SubTab = ({ v, l, Icon }) => (
    <button onClick={() => setSub(v)}
      data-testid={`convenios-subtab-${v}`}
      className={`text-sm px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-1.5 ${
        sub === v ? "border-[#0B2B62] bg-[#0B2B62] text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"
      }`}>
      <Icon className="w-3.5 h-3.5" /> {l}
    </button>
  );

  return (
    <div className="space-y-5" data-testid="admin-convenios-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            <h2 className="font-display font-bold text-xl text-[#0B2B62]">Convênios · Consultas</h2>
          </div>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Gestão de operadoras, planos e credenciamentos para teleconsulta.
            Módulo <b>independente</b> do bloco Convênios · Internação da Home.
            Profissional só aparece pra família quando o credenciamento está <b>Ativo</b>.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <SubTab v="insurers" l="Operadoras" Icon={Building2} />
        <SubTab v="plans" l="Planos" Icon={Layers} />
        <SubTab v="contracts" l="Contratos RAF" Icon={Handshake} />
        <SubTab v="credentials" l="Cred. Individual" Icon={ClipboardCheck} />
        <SubTab v="eligibility" l="Elegibilidade" Icon={FileCheck} />
        <SubTab v="authorizations" l="Autorizações" Icon={KeyRound} />
        <SubTab v="claims" l="Guias TISS" Icon={FileText} />
        <SubTab v="batches" l="Lotes" Icon={Package} />
        <SubTab v="denials" l="Glosas" Icon={AlertTriangle} />
        <SubTab v="reports" l="Relatórios" Icon={BarChart3} />
      </div>
      {sub === "insurers" && <InsurersSubTab insurers={insurers} onReload={loadInsurers} />}
      {sub === "plans" && <PlansSubTab insurers={insurers} />}
      {sub === "contracts" && <ContractsSubTab insurers={insurers} />}
      {sub === "credentials" && <CredentialsSubTab />}
      {sub === "eligibility" && <EligibilityTab insurers={insurers} />}
      {sub === "authorizations" && <AuthorizationsTab insurers={insurers} />}
      {sub === "claims" && <ClaimsTab insurers={insurers} />}
      {sub === "batches" && <BatchesTab insurers={insurers} />}
      {sub === "denials" && <DenialsTab />}
      {sub === "reports" && <ReportsTab />}
    </div>
  );
};

export default AdminConveniosPanel;
